import { AccountingModel, EntityType } from '@/constants/organization';
import { CountryCode } from '@/types/location';
import { z } from 'zod';

export const OssType = {
  UNION: 'UNION',
  NON_UNION: 'NON_UNION',
} as const;

export const organizationSettingsSchema = z.object({
  disable_onboarding: z.boolean(),
  enable_eu_registration: z.boolean().nullable(),
  disable_ai_classification: z.boolean().nullable(),
  oss_scheme: z.enum(OssType).nullable(),
  enable_new_auto_filing_experience: z.boolean(),
  // Admin-only: the backend exposes this on the admin settings endpoints and
  // deliberately keeps it off the organization-facing response, so the org app
  // never receives it. Defaulted so a pre-field backend response still parses.
  auto_file_cadence: z.boolean().default(false),
  // Unused leftover after KA-1928. Readable on GET until the column is dropped;
  // stripped from the update schema so the dead kill-switch cannot be flipped.
  late_approval_skip: z.boolean().default(false),
  // Admin-only write; readable on OrganizationSettingsResponse for import forms.
  allow_current_period_import: z.boolean().default(false),
  prefer_collected_tax_amounts: z.boolean(),
  special_tax_circumstances: z.boolean(),
  enable_business_address_fallback: z.boolean().nullable(),
  enable_vat_accounts_payable: z.boolean(),
  enable_use_tax: z.boolean(),
  file_upload_v2_enabled: z.boolean(),
  enable_ca_qp_tax_liability_display: z.boolean(),
  vda_fee: z.number().int().nonnegative().default(3000),
  enabled_jurisdictions: z.preprocess(v => (v == null ? ['US'] : v), z.array(z.string())),
  id: z.string(),
  created_at: z.string(),
  updated_at: z.string().nullable(),
  organization_id: z.string(),
  organization_name: z.string().nullable(),
});

export type OrganizationSettings = z.infer<typeof organizationSettingsSchema>;

export const enabledJurisdictionsResponseSchema = z.object({
  enabled_jurisdictions: z.preprocess(v => (v == null ? ['US'] : v), z.array(z.string())),
});

export type EnabledJurisdictionsResponse = z.infer<typeof enabledJurisdictionsResponseSchema>;

export const packagingCapabilitySchema = z.enum([
  'enable_use_tax',
  'enable_ecm',
  'enable_custom_analytics',
  'enable_filing_ready_reports',
  'enable_l2',
  'enable_api_keys',
  'enable_kintsugi_mail',
  'enable_managed_registrations',
  'enable_managed_filings',
]);

export type PackagingCapability = z.infer<typeof packagingCapabilitySchema>;

export const packagingCapabilitiesResponseSchema = z.object({
  capabilities: z.array(
    z.object({
      capability: packagingCapabilitySchema,
      enabled: z.boolean(),
      editable: z.boolean(),
    })
  ),
  can_enable: z.boolean(),
});

export type PackagingCapabilitiesResponse = z.infer<typeof packagingCapabilitiesResponseSchema>;

export const enabledJurisdictionsUpdateSchema = z.object({
  enabled_jurisdictions: z.array(z.string()).min(1, 'At least one jurisdiction is required'),
});

export type EnabledJurisdictionsUpdate = z.infer<typeof enabledJurisdictionsUpdateSchema>;

export const countryOptionSchema = z.object({
  code: z.string(),
  name: z.string(),
  region: z.string().optional(),
});

export type CountryOption = z.infer<typeof countryOptionSchema>;

export const availableCountriesResponseSchema = z.object({
  countries: z.array(countryOptionSchema),
});

export type AvailableCountriesResponse = z.infer<typeof availableCountriesResponseSchema>;

export const organizationSettingUpdateSchema = organizationSettingsSchema
  .pick({
    disable_onboarding: true,
    enable_eu_registration: true,
    disable_ai_classification: true,
    oss_scheme: true,
    enable_new_auto_filing_experience: true,
    auto_file_cadence: true,
    allow_current_period_import: true,
    prefer_collected_tax_amounts: true,
    special_tax_circumstances: true,
    enable_business_address_fallback: true,
    enable_vat_accounts_payable: true,
    enable_use_tax: true,
    file_upload_v2_enabled: true,
    enable_ca_qp_tax_liability_display: true,
    vda_fee: true,
    enabled_jurisdictions: true,
  })
  .extend({
    disable_onboarding: z.boolean().nullish(),
    enable_eu_registration: z.boolean().nullish(),
    disable_ai_classification: z.boolean().nullish(),
    oss_scheme: z.enum(OssType).nullish(),
    enable_new_auto_filing_experience: z.boolean().nullish(),
    auto_file_cadence: z.boolean().nullish(),
    allow_current_period_import: z.boolean().nullish(),
    prefer_collected_tax_amounts: z.boolean().nullish(),
    special_tax_circumstances: z.boolean().nullish(),
    enable_business_address_fallback: z.boolean().nullish(),
    enable_vat_accounts_payable: z.boolean().nullish(),
    enable_use_tax: z.boolean().nullish(),
    file_upload_v2_enabled: z.boolean().nullish(),
    enable_ca_qp_tax_liability_display: z.boolean().nullish(),
    vda_fee: z.number().int().nonnegative().nullish(),
    enabled_jurisdictions: z.array(z.string()).nullish(),
  });

export type OrganizationSettingUpdate = z.infer<typeof organizationSettingUpdateSchema>;

const BillingPlan = ['FREE', 'GROWTH', 'STARTER', 'SCALE', 'PREMIUM'] as const;
const BillingPreset = ['NONE', 'PREMIUM'] as const;
const OrganizationStatus = ['ACTIVE', 'ARCHIVED'] as const;

/** Admin GET `/organizations/{id}/downgrade/subscription` body. */
export const adminSubscriptionDowngradeStateSchema = z.object({
  status: z.string(),
  has_scheduled_changes: z.boolean(),
  scheduled_plan: z.enum(BillingPlan).nullable(),
  current_term_end: z.string().nullable(),
});

export type AdminSubscriptionDowngradeState = z.infer<typeof adminSubscriptionDowngradeStateSchema>;

export const organizationAdminUpdateSchema = z.object({
  // Display-only on Admin Settings; Downgrade / Configuration own status transitions.
  status: z.enum(OrganizationStatus).nullish(),
  // Display-only on Admin Settings; Chargebee owns paid plan writes.
  billing_plan: z.enum(BillingPlan).nullish(),
  billing_preset: z.enum(BillingPreset).nullish(),
  // Whole monthly USD dollars, exclusive of tax (same pattern as VDA Fee).
  billing_preset_price: z.number().int().positive().nullish(),
  is_test: z.boolean().nullish(),
  disable_emails: z.boolean().nullish(),
  internal_use: z.boolean().nullish(),
});

export type OrganizationAdminUpdate = z.infer<typeof organizationAdminUpdateSchema>;

const taxIdSchema = z.object({
  country_code: z.string().nullish(),
  tax_id: z.string().nullish(),
  state_code: z.string().nullish(),
});

// The three original counts are always present on the admin API response. The health-check
// fields below were added incrementally (KA-1513) and are optional so existing OrganizationDetails
// fixtures/consumers do not have to enumerate every new signal count; treat an absent count as 0.
const customerHealthSchema = z.object({
  unregistered_registrations: z.number(),
  invalid_address_count: z.number(),
  pending_product_count: z.number(),
  filings_to_approve: z.number().optional(),
  mail_not_setup: z.number().optional(),
  new_integration_pending: z.number().optional(),
  integration_issue_count: z.number().optional(),
  invalid_address_mom_growth_rate: z.number().optional(),
  products_unhealthy: z.number().optional(),
  exposure_30d: z.number().optional(),
  tax_variance_percent: z.number().optional(),
  last_login_days: z.number().optional(),
  unacknowledged_exposures: z.number().optional(),
  customer_engagement_issue: z.number().optional(),
});

export const organizationDetailsSchema = z.object({
  business_name: z.string().nullish(),
  entity_type: z.enum(EntityType).nullish(),
  dba: z.string().nullish(),
  incorporation_state: z.string().nullish(),
  incorporation_country: z.enum(CountryCode).nullish(),
  ein: z.string().nullish(),
  company_address_1: z.string().nullish(),
  company_address_2: z.string().nullish(),
  company_city: z.string().nullish(),
  company_state: z.string().nullish(),
  company_postal_code: z.string().nullish(),
  company_country_code: z.enum(CountryCode).nullish(),
  business_description: z.string().nullish(),
  // Admin GET returns [] when unset (BE serializer); nullish keeps fixtures soft.
  business_websites: z.array(z.string()).nullish(),
  home_state_registration: z.string().nullish(),
  first_operations_date: z.string().nullish(),
  business_phone: z.string().nullish(),
  business_email: z.email({ message: 'Invalid email format' }).or(z.literal('')).nullish(),
  business_address_1: z.string().nullish(),
  business_address_2: z.string().nullish(),
  business_city: z.string().nullish(),
  business_state: z.string().nullish(),
  business_postal_code: z.string().nullish(),
  business_country_code: z.enum(CountryCode).nullish(),
  business_mailing_address_1: z.string().nullish(),
  business_mailing_address_2: z.string().nullish(),
  business_mailing_city: z.string().nullish(),
  business_mailing_state: z.string().nullish(),
  business_mailing_postal_code: z.string().nullish(),
  business_mailing_country_code: z.enum(CountryCode).nullish(),
  business_fiscal_year_end: z.string().nullish(),
  accounting_model: z.enum(AccountingModel).nullish(),
  business_contact_name: z.string().nullish(),
  business_contact_phone: z.string().nullish(),
  business_contact_email: z.email({ message: 'Invalid email format' }).or(z.literal('')).nullish(),
  business_contact_dob: z.string().nullish(),
  business_contact_title: z.string().nullish(),
  business_contact_address_1: z.string().nullish(),
  business_contact_address_2: z.string().nullish(),
  business_contact_city: z.string().nullish(),
  business_contact_state: z.string().nullish(),
  business_contact_postal_code: z.string().nullish(),
  business_contact_country_code: z.enum(CountryCode).nullish(),
  business_contact_ssn: z.string().nullish(),
  business_contact_dl: z.string().nullish(),
  business_owner1_name: z.string().nullish(),
  business_owner1_phone: z.string().nullish(),
  business_owner1_email: z.email({ message: 'Invalid email format' }).or(z.literal('')).nullish(),
  business_owner1_dob: z.string().nullish(),
  business_owner1_title: z.string().nullish(),
  business_owner1_address_1: z.string().nullish(),
  business_owner1_address_2: z.string().nullish(),
  business_owner1_city: z.string().nullish(),
  business_owner1_state: z.string().nullish(),
  business_owner1_postal_code: z.string().nullish(),
  business_owner1_country_code: z.enum(CountryCode).nullish(),
  business_owner1_ssn: z.string().nullish(),
  business_owner1_dl: z.string().nullish(),
  business_owner1_percent_ownership: z.string().nullish(),
  business_owner2_name: z.string().nullish(),
  business_owner2_phone: z.string().nullish(),
  business_owner2_email: z.email({ message: 'Invalid email format' }).or(z.literal('')).nullish(),
  business_owner2_dob: z.string().nullish(),
  business_owner2_title: z.string().nullish(),
  business_owner2_address_1: z.string().nullish(),
  business_owner2_address_2: z.string().nullish(),
  business_owner2_city: z.string().nullish(),
  business_owner2_state: z.string().nullish(),
  business_owner2_postal_code: z.string().nullish(),
  business_owner2_country_code: z.enum(CountryCode).nullish(),
  business_owner2_ssn: z.string().nullish(),
  business_owner2_dl: z.string().nullish(),
  business_owner2_percent_ownership: z.string().nullish(),
  business_owner3_name: z.string().nullish(),
  business_owner3_phone: z.string().nullish(),
  business_owner3_email: z.email({ message: 'Invalid email format' }).or(z.literal('')).nullish(),
  business_owner3_dob: z.string().nullish(),
  business_owner3_title: z.string().nullish(),
  business_owner3_address_1: z.string().nullish(),
  business_owner3_address_2: z.string().nullish(),
  business_owner3_city: z.string().nullish(),
  business_owner3_state: z.string().nullish(),
  business_owner3_postal_code: z.string().nullish(),
  business_owner3_country_code: z.enum(CountryCode).nullish(),
  business_owner3_ssn: z.string().nullish(),
  business_owner3_dl: z.string().nullish(),
  business_owner3_percent_ownership: z.string().nullish(),
  id: z.string(),
  email_group: z.string().nullish(),
  auto_file: z.boolean().nullish(),
  auto_register: z.boolean().nullish(),
  physical_mail_address_status: z.string().nullish(),
  physical_mail_address_line1: z.string().nullish(),
  physical_mail_address_line2: z.string().nullish(),
  physical_mail_address_city: z.string().nullish(),
  physical_mail_address_state: z.string().nullish(),
  physical_mail_address_postal_code: z.string().nullish(),
  physical_mail_address_external_id: z.string().nullish(),
  physical_mail_address_display: z.string().nullish(),
  partner_id: z.string().nullish(),
  tax_ids: z.array(taxIdSchema).nullish(),
  naics: z.string().nullish(),
  customer_health: customerHealthSchema,
  premium_upgrade_triggered: z.boolean().nullish(),
  prefer_collected_tax_amounts: z.boolean().nullish(),
  onboarding_link: z.string().nullish(),
  do_not_file: z.boolean().nullish(),
  health_check: z.string().nullish(),
  csm_user_id: z.string().nullish(),
  target_archive_date: z.string().nullish(),
  last_health_check_completed_at: z.string().nullish(),
  last_health_check_status: z.string().nullish(),
  open_follow_up_count: z.number().optional(),
  open_health_check_task_id: z.string().nullish(),
  health_check_in_progress: z.boolean().optional(),
  // Optional so existing test fixtures typed as OrganizationDetails stay valid.
  // The admin details API always returns this once the review PR is deployed.
  recent_dispositions: z
    .array(
      z.object({
        signal_key: z.string(),
        label: z.string(),
        disposition: z.string(),
        note: z.string().nullish(),
        follow_up_owner: z.string().nullish(),
        follow_up_due_date: z.string().nullish(),
        follow_up_open: z.boolean().optional(),
        suppressed_until: z.string().nullish(),
      })
    )
    .optional(),
  /** Import marketplace channels (read-only; admin API). Pending channels include "(pending review)". */
  connected_marketplace_names: z.array(z.string()).default([]),
});

export type OrganizationDetails = z.infer<typeof organizationDetailsSchema>;

export const organizationDetailsUpdateSchema = organizationDetailsSchema
  .omit({
    id: true,
    customer_health: true,
    premium_upgrade_triggered: true,
    prefer_collected_tax_amounts: true,
    partner_id: true,
    connected_marketplace_names: true,
    last_health_check_completed_at: true,
    last_health_check_status: true,
    open_follow_up_count: true,
    open_health_check_task_id: true,
    health_check_in_progress: true,
    recent_dispositions: true,
    tax_ids: true, // Remove tax_ids array from update schema
    business_websites: true, // V1 admin read-only (CP-4639)
  })
  .extend({
    // Add tax_id as a string for update payload
    tax_id: z.string().nullish(),
  });

export type OrganizationDetailsUpdate = z.infer<typeof organizationDetailsUpdateSchema>;
